import { Link } from 'react-router-dom';
import { ArrowUpRight, Bot, Brain, Cpu, BookOpen, RefreshCw, Gauge, ArrowRight } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import LeadForm from '@/components/shared/LeadForm';
import { PLACEHOLDERS } from '@/lib/constants';

const aiServices = [
  { icon: Gauge, title: 'AI Readiness & HR Diagnostics', desc: 'Where is your organisation on the AI maturity curve? Our diagnostic assesses people readiness, process gaps, and change capacity.' },
  { icon: Brain, title: 'AI Consulting & Strategy', desc: 'Build an AI strategy that starts with people, not technology. Identify use cases, assess risks, and create an executable roadmap.' },
  { icon: Cpu, title: 'AI Agents & Implementation', desc: 'From pilot to production. Build and deploy AI agents that augment human work. HR automation, talent analytics, intelligent workflows.' },
  { icon: BookOpen, title: 'AI-Powered Learning', desc: 'Personalised learning at scale. AI integrated into your L&D ecosystem for adaptive, context-aware learning experiences.' },
  { icon: RefreshCw, title: 'Change Management for AI', desc: 'The biggest barrier to AI adoption isn\'t technology. It\'s people. Structured change management for AI adoption.' },
];

function FadeIn({ children, delay = 0 }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  return <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: 'easeOut' }}>{children}</motion.div>;
}

export default function BotsAIPage() {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end bg-[#050A14] overflow-hidden" data-testid="botsai-hub">
        <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#2563EB]/15 rounded-full blur-[150px] pointer-events-none" />
        <div className="absolute bottom-10 left-10 w-[300px] h-[300px] bg-[#2563EB]/8 rounded-full blur-[100px] pointer-events-none" />
        <div className="noise-overlay absolute inset-0 pointer-events-none" />

        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12 pb-16 lg:pb-24 pt-32">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-[#2563EB]/15 text-[#60A5FA] px-4 py-2 rounded-full text-[12px] font-medium tracking-wide mb-8">
                <Bot size={14} /> BOTS.AI Practice
              </div>
              <h1 className="text-[clamp(2.5rem,5.5vw,5rem)] font-bold text-white leading-[0.95] tracking-[-0.02em] mb-6">
                AI that works <span className="text-[#60A5FA]">with</span> your<br/>people, not against them.
              </h1>
              <p className="text-lg text-white/45 leading-[1.7] max-w-lg mb-10">
                Everyone's talking about AI. Few are doing it right. The gap isn't technology. It's people, process, and change.
              </p>
              <Link to="/contact" className="group inline-flex items-center gap-2.5 bg-[#2563EB] text-white px-8 py-4 rounded-full text-[14px] font-medium hover:bg-[#1D4ED8] transition-colors">
                Assess your AI readiness <ArrowUpRight size={15} className="opacity-70 group-hover:opacity-100 transition-opacity" />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-5 hidden lg:block">
              <div className="aspect-square rounded-[2rem] overflow-hidden bg-[#2563EB]/5 border border-[#2563EB]/10">
                <img src={PLACEHOLDERS.technology} alt="AI" className="w-full h-full object-cover opacity-50" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI SERVICES */}
      <section className="py-24 lg:py-32 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <p className="text-[13px] font-medium text-[#2563EB] tracking-[0.2em] uppercase mb-5">Practice Areas</p>
            <h2 className="text-4xl font-bold text-[#1A1008] leading-[1.1] tracking-tight mb-16">What We Do in AI</h2>
          </FadeIn>
          <div className="space-y-1.5">
            {aiServices.map((svc, i) => {
              const Icon = svc.icon;
              return (
                <FadeIn key={svc.title} delay={i * 0.03}>
                  <div className="group flex flex-col lg:flex-row lg:items-center justify-between gap-4 py-6 lg:py-7 px-6 lg:px-8 border-b border-[#3D1C11]/6 hover:bg-white rounded-xl transition-all duration-300 hover:border-transparent hover:shadow-lg hover:shadow-[#3D1C11]/3">
                    <div className="flex items-center gap-5 flex-1">
                      <div className="w-10 h-10 rounded-xl bg-[#2563EB]/8 flex items-center justify-center flex-shrink-0">
                        <Icon size={18} className="text-[#2563EB]" />
                      </div>
                      <h3 className="text-lg font-semibold text-[#1A1008] group-hover:text-[#2563EB] transition-colors tracking-tight">{svc.title}</h3>
                    </div>
                    <p className="text-[14px] text-[#6B5B50] max-w-sm leading-relaxed pl-[60px] lg:pl-0 lg:text-right">{svc.desc}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="bg-[#050A14] py-24 lg:py-32 relative">
        <div className="absolute top-10 right-20 w-48 h-48 bg-[#2563EB]/10 rounded-full blur-[80px] pointer-events-none" />
        <div className="noise-overlay absolute inset-0 pointer-events-none" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight tracking-tight mb-5">AI is moving fast.</h2>
              <p className="text-[15px] text-white/40 leading-[1.7]">Your people strategy shouldn't lag behind. Talk to our AI practice.</p>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <div className="bg-white rounded-2xl p-8 lg:p-10">
                <LeadForm sourceForm="botsai-hub" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
